import React, { FormEvent, useState } from "react";
import {
  Box,
  Button,
  Form,
  FormField,
  Input,
  Modal,
  SpaceBetween,
} from "@amzn/awsui-components-react-v3";
import {
  sanitizeRevocationReason,
  MAX_REVOCATION_REASON_LENGTH,
} from "../utils/input-validation";

interface RevokeCredentialConfirmationModalProps {
  alias: string;
  credentialHint: string;
  loading: boolean;
  onDismiss: () => void;
  onConfirm: (reason: string) => void;
}

interface FormState {
  reason: string;
  hasSubmittedAtLeastOnce: boolean;
}

const initialFormState: FormState = {
  reason: "",
  hasSubmittedAtLeastOnce: false,
};

export const RevokeCredentialConfirmationModal = ({
  alias,
  credentialHint,
  loading,
  onDismiss,
  onConfirm,
}: RevokeCredentialConfirmationModalProps) => {
  const [formState, setFormState] = useState(initialFormState);

  const reasonError =
    formState.reason.trim().length === 0 ? "Revocation reason is required" : "";

  const handleDismiss = () => {
    setFormState(initialFormState);
    onDismiss();
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!reasonError) {
      onConfirm(formState.reason.trim());
    } else {
      setFormState((prev) => ({ ...prev, hasSubmittedAtLeastOnce: true }));
    }
  };

  return (
    <Modal
      visible={true}
      onDismiss={handleDismiss}
      header="Revoke credential"
      data-testid="revoke-credential-modal"
    >
      <form onSubmit={handleSubmit}>
        <Form
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button formAction="none" variant="link" onClick={handleDismiss}>
                Cancel
              </Button>
              <Button
                variant="primary"
                formAction="submit"
                loading={loading}
                data-testid="confirm-revoke-button"
              >
                Revoke
              </Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween direction="vertical" size="l">
            <Box>
              You are revoking credential{" "}
              <Box variant="code">{credentialHint}</Box> for{" "}
              <strong>{alias}</strong>. This action cannot be undone.
            </Box>
            <FormField
              label="Revocation reason"
              errorText={formState.hasSubmittedAtLeastOnce ? reasonError : ""}
              warningText={
                formState.reason.length >= MAX_REVOCATION_REASON_LENGTH
                  ? `Maximum reason length reached (${MAX_REVOCATION_REASON_LENGTH} characters).`
                  : ""
              }
              data-testid="revoke-reason-form-field"
            >
              <Input
                value={formState.reason}
                onChange={({ detail }) =>
                  setFormState((prev) => ({
                    ...prev,
                    reason: sanitizeRevocationReason(detail.value),
                  }))
                }
                placeholder="e.g. Key lost, key reset, replacing with new key"
                data-testid="revoke-reason-input"
                autoComplete={false}
              />
            </FormField>
          </SpaceBetween>
        </Form>
      </form>
    </Modal>
  );
};
